import React, {useState} from 'react';
import {useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBars, faMagnifyingGlass, faXmark, faMoon, faSun} from '@fortawesome/free-solid-svg-icons';
import {useTheme} from '../../contexts/ThemeContext';
import {AppNotification} from '../../types/notification';
import {findActiveNav} from './navItems';
import {GlobalSearch} from './GlobalSearch';
import {NotificationBell} from './NotificationBell';
import {UserMenu} from './UserMenu';

interface AppHeaderProps {
    onOpenSidebar: () => void;
    notifications: AppNotification[];
}

/**
 * Thanh trên cùng: menu (mobile) · tên trang · tìm kiếm · chuông · avatar.
 *
 * Trên điện thoại ô tìm kiếm không đứng cố định - nó chiếm gần hết chiều ngang
 * và đẩy tên trang ra khỏi màn hình. Thay vào đó là nút kính lúp, bấm mới bung
 * ra thành một hàng riêng bên dưới.
 */
export const AppHeader: React.FC<AppHeaderProps> = ({onOpenSidebar, notifications}) => {
    const location = useLocation();
    const {isDarkMode, toggleDarkMode} = useTheme();
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const active = findActiveNav(location.pathname);
    const isRoomDetail = /^\/phong\/[^/]+$/.test(location.pathname);

    // `.safe-top`: ở chế độ cài về màn hình chính, trang phủ luôn cả dải đồng
    // hồ/pin của iPhone (viewport-fit=cover), nên header dính top-0 sẽ nằm ngay
    // dưới đồng hồ nếu không chừa inset - xem index.css.
    return (
        <header className="sticky top-0 z-30 glass-toolbar border-b safe-top">
            {/* `min-h-16` chứ không `h-16`: ở cỡ chữ lớn tiêu đề text-3xl cao hơn
                64px và bị cắt cụt trong chiều cao cứng. Cho nó cao thêm vài pixel
                vẫn hơn là mất nửa dòng chữ - xem chú thích ở index.css. */}
            <div className="min-h-16 px-3 sm:px-5 py-2 flex items-center gap-2 sm:gap-3">
                <button
                    onClick={onOpenSidebar}
                    className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                    aria-label="Mở menu"
                >
                    <FontAwesomeIcon icon={faBars} className="w-5 h-5"/>
                </button>

                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {active && (
                        <span className="hidden sm:flex w-10 h-10 rounded-lg flex-shrink-0 items-center justify-center
                            bg-slate-500/15 text-slate-600 dark:text-slate-300">
                            <FontAwesomeIcon icon={active.icon} className="w-5 h-5"/>
                        </span>
                    )}
                    <div className="min-w-0">
                        <h1 className="font-bold tracking-tight text-2xl sm:text-3xl text-slate-900 dark:text-white truncate">
                            {active?.label ?? 'Nha48.com'}
                        </h1>
                        <p className="hidden sm:block text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5">
                            {isRoomDetail ? 'Chi tiết phòng' : active?.subtitle ?? 'Quản lý điện nước & thanh toán'}
                        </p>
                    </div>
                </div>

                {/* Tìm kiếm: cố định trên desktop, thu thành nút trên mobile */}
                <GlobalSearch className="hidden md:block w-56 lg:w-72 flex-shrink-0"/>

                <button
                    onClick={() => setIsSearchOpen(open => !open)}
                    className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                    aria-label={isSearchOpen ? 'Đóng tìm kiếm' : 'Tìm kiếm'}
                    aria-expanded={isSearchOpen}
                >
                    <FontAwesomeIcon icon={isSearchOpen ? faXmark : faMagnifyingGlass} className="w-5 h-5"/>
                </button>

                {/* Đổi giao diện sáng/tối: nút riêng trên desktop, trong menu avatar trên mobile */}
                <button
                    onClick={toggleDarkMode}
                    className="hidden sm:flex w-10 h-10 rounded-lg items-center justify-center flex-shrink-0
                        text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                    aria-label={isDarkMode ? 'Chuyển chế độ sáng' : 'Chuyển chế độ tối'}
                >
                    <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="w-5 h-5"/>
                </button>

                <NotificationBell notifications={notifications}/>
                <UserMenu/>
            </div>

            {isSearchOpen && (
                <div className="md:hidden px-3 pb-3">
                    <GlobalSearch/>
                </div>
            )}
        </header>
    );
};
